/**
 * Exercising an option written into a trade.
 *
 * THE CLAUSE OWNS NOTHING UNTIL SOMEBODY USES IT. The 19 November 2025 deal carries "JOHN: right
 * to swap 6th rd picks in 2026 (John may decline)". The band prints it verbatim, the ownership
 * file lists it under `swapOptions`, and neither moves a pick — correctly, because until the order
 * is set nobody knows whether John wants Rajan's sixth. Tim, 3 September, the order published:
 * "now that the draft is set, I need to exercise John's option."
 *
 * An exercise IS A TRADE — two picks change hands on a date — so it is written as one, through the
 * same settlement every other trade goes through: a ledger row, pick ownership derived from it,
 * keepers standing on either pick re-charged by 2015-III-7. What is different is that it has no
 * window. The league already had its 48 hours on the trade the option came out of; the swap is
 * a term of that trade being performed, not a new deal for bystanders to counter or veto. So the
 * doc is born agreed and settles at once.
 *
 * This file is the arithmetic: who the option belongs to (read off the note), which round, and
 * whether the swap can still be made — a man who has since traded his own pick in that round has
 * nothing to swap. The page does the writing.
 */

import type { LiveTradeDoc, LiveTradeSide } from '../store/types.js';
import { pickLabel, slotOf } from './picks.js';

export const ORDINAL = ['', '1st', '2nd', '3rd', '4th', '5th', '6th', '7th', '8th', '9th', '10th',
  '11th', '12th', '13th', '14th', '15th', '16th', '17th', '18th', '19th', '20th'];

export const ordinal = (n: number): string => ORDINAL[n] ?? `${n}th`;

/** The two managers on a row, upper-cased, and the clause text the sheet stored. */
export interface OptionRow {
  id: string;
  date: string | null;
  sides: readonly [{ gm: string }, { gm: string }];
  notes?: readonly string[];
}

/**
 * Whose option it is.
 *
 * The sheet prefixes the note with the GM it belongs to — "JOHN: right to swap…" — and where it
 * does not, the clause names him in its own words ("John may decline"). A note naming neither
 * party is somebody's, but not knowably, and the page asks.
 */
export function optionHolder(row: OptionRow, short: (gm: string) => string): string | null {
  const text = (row.notes ?? []).join(' ');
  const gms = row.sides.map((s) => s.gm.toUpperCase());
  const prefix = /^([A-Z]{2,8}):/.exec(text);
  if (prefix && gms.includes(prefix[1]!)) return prefix[1]!;
  const named = gms.filter((g) => new RegExp(`\\b${short(g)}\\b`, 'i').test(text));
  return named.length === 1 ? named[0]! : null;
}

/** The round the clause is about — "6th rd", "round 6", "6th-round" — or null when it does not say. */
export function optionRound(row: OptionRow): number | null {
  const text = (row.notes ?? []).join(' ');
  const ord = /\b(\d{1,2})(?:st|nd|rd|th)\b/i.exec(text);
  if (ord) return Number(ord[1]);
  const word = /\bround\s+(\d{1,2})\b/i.exec(text);
  return word ? Number(word[1]) : null;
}

export interface SwapPlan {
  ok: boolean;
  /** Why not, in the words the page prints. */
  why?: string;
  /** The two sides, holder first, each sending his own pick in the round. */
  sides?: [LiveTradeSide, LiveTradeSide];
  /** What each man is giving up, by code where the order is known. */
  gives?: Record<string, string>;
}

/**
 * The swap as a pair of sends, or the reason it cannot be made.
 *
 * Each man sends the pick off HIS OWN seat in that round — that is what "swap 6th-round picks"
 * means, and it is also the only reading `pickTransfersFrom` can act on without an origin
 * selector. A seat whose pick in that round now belongs to somebody else has nothing to send, and
 * the plan says whose it became rather than writing a trade the board cannot execute.
 */
export function swapPlan(
  by: string, other: string, round: number, season: number,
  ownership: ReadonlyMap<string, string>, order: readonly string[], teams: number,
  short: (gm: string) => string,
): SwapPlan {
  const a = by.toUpperCase();
  const b = other.toUpperCase();
  if (a === b) return { ok: false, why: 'The two sides of a swap have to be different managers.' };
  if (!Number.isInteger(round) || round < 1) return { ok: false, why: 'Name the round.' };
  for (const gm of [a, b]) {
    const holder = ownership.get(`${round}:${gm}`) ?? gm;
    if (holder !== gm) {
      return {
        ok: false,
        why: `${short(gm)} no longer holds his own ${ordinal(round)} — it is ${short(holder)}’s now, `
          + 'so there is nothing on his side to swap.',
      };
    }
  }
  const text = (gm: string) => {
    const slot = order.length ? slotOf(order, gm) : null;
    return slot === null ? `${season} ${ordinal(round)}` : `${season} ${pickLabel(round, slot, teams)}`;
  };
  return {
    ok: true,
    sides: [
      { gm: a, sends: [{ kind: 'pick', text: text(a), origin: a }] },
      { gm: b, sends: [{ kind: 'pick', text: text(b), origin: b }] },
    ],
    gives: { [a]: text(a), [b]: text(b) },
  };
}

/** A date the sheet stored, said the way the league says it — "Nov 19, 2025". */
export function sayDate(iso: string | null): string {
  if (!iso) return 'undated';
  const d = new Date(`${iso}T12:00:00Z`);
  if (Number.isNaN(d.getTime())) return iso;
  return d.toLocaleDateString('en-US', { day: 'numeric', month: 'short', year: 'numeric', timeZone: 'UTC' });
}

/**
 * The live-trade doc an exercise files: agreed by the commissioner at the instant it is recorded,
 * no counters, no vetoes, one clause saying which option it performs. Settlement writes the
 * ledger row from it exactly as it would for any other trade.
 */
export function exerciseDoc(
  row: OptionRow, sides: [LiveTradeSide, LiveTradeSide],
  round: number, season: number, actor: string, at: string, short: (gm: string) => string,
): LiveTradeDoc {
  const by = sides[0].gm;
  return {
    id: `option-${row.id}-${at.replace(/[^0-9]/g, '').slice(0, 14)}`,
    season,
    sides,
    filedBy: actor,
    filedAt: at,
    agreedAt: at,
    agreedSource: 'commissioner',
    agreedBy: actor,
    counters: [],
    vetoes: [],
    clauses: [
      `${short(by)} exercised the ${ordinal(round)}-round swap from the ${sayDate(row.date)} trade`,
    ],
  };
}
